export const MEMBERSHIP_STATUSES = Object.freeze([
  "active",
  "inactive",
] as const)

export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number]
export type HistoricalMembershipStatus = MembershipStatus | "approved"

export const USER_STATUSES = MEMBERSHIP_STATUSES
export type UserStatus = MembershipStatus

/** Validate untrusted persisted values at membership-domain boundaries. */
export const normalizeMembershipStatus = (
  status: string | null | undefined,
): MembershipStatus | null => {
  if (status === "active" || status === "inactive") return status
  return null
}

export const isActiveMembershipStatus = (
  status: string | null | undefined,
): boolean => normalizeMembershipStatus(status) === "active"

export const PERSON_STATUSES = MEMBERSHIP_STATUSES
export type PersonStatus = MembershipStatus

/**
 * Valid status values for V3Invoice entities
 */
export const INVOICE_STATUSES = Object.freeze([
  "draft",
  "sent",
  "open",
  "paid",
  "void",
  "uncollectible",
] as const)

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number]

/** Finalized invoices eligible for full bank-payment confirmation. */
export const BANK_PAYABLE_INVOICE_STATUSES = Object.freeze([
  "open",
  "sent",
  "uncollectible",
] as const satisfies readonly InvoiceStatus[])

/** Invoice statuses whose deficient hours remain settled by an invoice. */
export const INVOICE_HOUR_DEDUCTION_STATUSES = Object.freeze([
  "draft",
  "sent",
  "open",
  "paid",
  "uncollectible",
] as const satisfies readonly InvoiceStatus[])

/**
 * Invoice statuses that cause task assignment locking.
 * Invoices in these statuses block (or warn) assignment changes
 * that impact billed entities.
 */
export const LOCKING_INVOICE_STATUSES = Object.freeze([
  "draft",
  "open",
  "paid",
] as const)

export type LockingInvoiceStatus = (typeof LOCKING_INVOICE_STATUSES)[number]

/**
 * Invoice statuses that do NOT cause task assignment locking.
 * Invoices in these statuses are ignored during lock evaluation.
 */
export const NON_LOCKING_INVOICE_STATUSES = Object.freeze([
  "void",
  "uncollectible",
] as const)

export type NonLockingInvoiceStatus =
  (typeof NON_LOCKING_INVOICE_STATUSES)[number]

/**
 * Task filter categories for UI
 */
export const TASK_CATEGORIES = Object.freeze([
  "upcoming",
  "completed",
  "available",
] as const)

export type TaskCategory = (typeof TASK_CATEGORIES)[number]
